import { motion } from "framer-motion";
import { IMAGES } from "../lib/tokens";

/**
 * THE TITLE SEQUENCE — v3 for the Ghost World hero (12 Feb 2026).
 *
 * Founder's re-order after the widescreen swap:
 *
 *   • Logo upper-left  — small brand anchor (the seal on the centre hub carries
 *                        the same mark, so this one whispers, not shouts).
 *   • Welcome headline — ENDEAVOR TO DESIGN, BUILD & THRIVE (top-centre, hero).
 *   • Promise          — "Your Authentic Intelligence meets Assistive…"
 *                        (right under the headline, one line box).
 *   • After-statement  — ADULTING FINALLY HAS A WORLD (bottom-centre,
 *                        heavier weight, delivered LAST — the punchline).
 *
 * Every block is `pointer-events: none` on its outer wrapper so it never eats
 * an orb click; only the logo itself is interactive.
 *
 * ?claim=ambition still swaps the after-statement word for A/B comparison.
 */

const claimWord = () => {
  if (typeof window === "undefined") return "ADULTING";
  return new URLSearchParams(window.location.search).get("claim") === "ambition"
    ? "AMBITION"
    : "ADULTING";
};

const SHADOW =
  "0 2px 10px rgba(0,0,0,1), 0 0 22px rgba(0,0,0,1), 0 0 44px rgba(0,0,0,0.98), 0 0 78px rgba(0,0,0,0.9), 0 0 130px rgba(0,0,0,0.6)";
const SOFT =
  "0 1px 4px rgba(0,0,0,1), 0 0 12px rgba(0,0,0,1), 0 0 28px rgba(0,0,0,0.95), 0 0 54px rgba(0,0,0,0.75)";

export const TopMessage = ({ beat = 0.5 }) => {
  return (
    <>
      {/* Logo — upper-left corner, small. Dark-field lockup with screen blend
          so the parchment on the plate never fights the mark. */}
      <motion.div
        className="pointer-events-none absolute left-4 top-3 z-30 md:left-8 md:top-5"
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.2, delay: beat }}
        data-testid="atlas-corner-mark"
      >
        <img
          src="https://customer-assets-jt897jd0.emergentagent.net/job_melzai-world/artifacts/kir7cz1x_Endeavorum-logo-fonted.png"
          alt="Endeavorum"
          className="block w-[110px] md:w-[140px] lg:w-[160px]"
          style={{
            filter:
              "drop-shadow(0 3px 8px rgba(0,0,0,0.95)) drop-shadow(0 0 22px rgba(0,0,0,0.9)) drop-shadow(0 0 44px rgba(0,0,0,0.6))",
          }}
        />
      </motion.div>

      {/* The welcome headline — top-centre, hero. This is the promise you
          make the visitor before you tell them where they are. Widths are
          capped so the line CAN'T overflow at any viewport, then the clamp
          scales the type down inside that cap. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-30 flex flex-col items-center gap-[1.1vh] px-6 pt-[2.4vh] text-center md:pt-[3.2vh]"
        data-testid="atlas-offer"
      >
        <motion.p
          className="engrave font-bold uppercase text-[#f2c877]"
          style={{ letterSpacing: "0.13em", textShadow: SHADOW }}
          data-testid="offer-invitation"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3, delay: beat + 0.2 }}
        >
          <span className="mx-auto block max-w-[min(92vw,1180px)] text-[clamp(0.98rem,2.1vw,2.15rem)] leading-[1.05]">
            Endeavor to Design, Build &amp; Thrive
          </span>
        </motion.p>

        <motion.p
          className="serif font-semibold leading-[1.4] text-[#f2dcaa]"
          style={{ textShadow: SOFT }}
          data-testid="offer-sub"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: beat + 0.5 }}
        >
          <span className="mx-auto block max-w-[min(92vw,1180px)] text-[clamp(15px,1.55vw,26px)] leading-[1.35]">
            Your <span className="text-[#f8e6b4]">Authentic Intelligence</span> meets{" "}
            <span className="text-[#f8e6b4]">Assistive Intelligence</span> in Places to
            Explore &amp; Spaces for Clarity
          </span>
        </motion.p>
      </div>

      {/* The after-statement — DEPRECATED from TopMessage as of 12 Feb 2026.
          It now lives inside <world-plate> so it sits on the World image
          instead of the walnut background below it. See Atlas.jsx. */}
    </>
  );
};

export default TopMessage;
